/**
 * Phase C — formatter for the Trained badge popover's relative-time
 * line ("Trained 2 days ago"). Pulled out of the component so vitest
 * can pin the bucketing rules without booting React.
 *
 * Buckets (all rounded down):
 *   - < 60s          → "just now"
 *   - < 60min        → "N minutes ago" (singular "1 minute ago")
 *   - < 24h          → "N hours ago"   (singular "1 hour ago")
 *   - < 30 days      → "N days ago"    (singular "1 day ago")
 *   - else           → ISO-date "on YYYY-MM-DD" (deterministic, no
 *                     locale-driven surprises in screenshots)
 *
 * Returns an empty string when `completedAt` is missing/zero so the
 * popover can `{x && <div>…</div>}` without leaking an "ago" line.
 */

const MS_MIN = 60_000;
const MS_HOUR = 3_600_000;
const MS_DAY = 86_400_000;
const MS_30D = 30 * MS_DAY;

export function formatTrainedRelative(
  completedAtMs: number | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (!completedAtMs || completedAtMs <= 0) return '';
  const delta = Math.max(0, nowMs - completedAtMs);
  if (delta < MS_MIN) return 'Trained just now';
  if (delta < MS_HOUR) {
    const n = Math.floor(delta / MS_MIN);
    return n === 1 ? 'Trained 1 minute ago' : `Trained ${n} minutes ago`;
  }
  if (delta < MS_DAY) {
    const n = Math.floor(delta / MS_HOUR);
    return n === 1 ? 'Trained 1 hour ago' : `Trained ${n} hours ago`;
  }
  if (delta < MS_30D) {
    const n = Math.floor(delta / MS_DAY);
    return n === 1 ? 'Trained 1 day ago' : `Trained ${n} days ago`;
  }
  // ISO date — month is 0-indexed; we want YYYY-MM-DD.
  const d = new Date(completedAtMs);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `Trained on ${yyyy}-${mm}-${dd}`;
}
